import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, RefreshCw } from 'lucide-react'
import { createPayingAgent, fetchPayingAgentOnboardings } from '../../services/api'
import Loader from '../../components/Loader'
import Pagination from '../../components/Pagination'
import {
	Alert,
	Badge,
	Button,
	Card,
	EmptyState,
	PageHeader,
	Select,
	TextInput,
	formatDateTime,
	formatDocument,
	statusTone,
} from '../../components/ui'

const ORDER_COLUMNS = [
	{ value: 'id', label: 'ID' },
	{ value: 'createdAt', label: 'Data de criacao' },
]

const ORDER_DIRECTIONS = [
	{ value: 'ASC', label: 'Crescente' },
	{ value: 'DESC', label: 'Decrescente' },
]

const CreateModal = ({ onClose, onCreated }) => {
	const [form, setForm] = useState({ accountPublicId: '', commissionAccountPublicId: '' })
	const [isSaving, setIsSaving] = useState(false)
	const [error, setError] = useState('')

	const handleChange = (e) => {
		const { name, value } = e.target
		setForm((prev) => ({ ...prev, [name]: value }))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setIsSaving(true)
		setError('')
		try {
			await createPayingAgent({
				accountPublicId: form.accountPublicId.trim(),
				commissionAccountPublicId: form.commissionAccountPublicId.trim(),
			})
			onCreated('Associacao de conta criada com sucesso')
		} catch (err) {
			setError(err.message)
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4">
			<div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
				<div className="border-b border-gray-200 px-5 py-4">
					<h2 className="text-base font-semibold text-gray-900">
						Criar associacao de conta
					</h2>
					<p className="mt-0.5 text-sm text-gray-500">
						POST /backoffice/paying-agents — vincula duas contas ja existentes direto,
						sem passar por um onboarding novo.
					</p>
				</div>

				<form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
					<Alert onDismiss={() => setError('')}>{error}</Alert>

					<TextInput
						id="accountPublicId"
						name="accountPublicId"
						label="Public ID da conta"
						placeholder="11111111-1111-1111-1111-111111111111"
						required
						value={form.accountPublicId}
						onChange={handleChange}
					/>
					<TextInput
						id="commissionAccountPublicId"
						name="commissionAccountPublicId"
						label="Public ID da conta de comissao"
						placeholder="22222222-2222-2222-2222-222222222222"
						required
						value={form.commissionAccountPublicId}
						onChange={handleChange}
					/>

					<div className="flex justify-end gap-2 pt-2">
						<Button type="button" variant="secondary" onClick={onClose}>
							Cancelar
						</Button>
						<Button type="submit" disabled={isSaving}>
							{isSaving ? <Loader size="sm" /> : <Plus className="h-4 w-4" />}
							Criar
						</Button>
					</div>
				</form>
			</div>
		</div>
	)
}

const PayingAgentsList = () => {
	const navigate = useNavigate()
	const [page, setPage] = useState(1)
	const [order, setOrder] = useState({ orderColumn: 'id', orderDirection: 'ASC' })
	const [data, setData] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')
	const [feedback, setFeedback] = useState('')
	const [showCreate, setShowCreate] = useState(false)

	const load = useCallback(async () => {
		setIsLoading(true)
		setError('')
		try {
			setData(await fetchPayingAgentOnboardings({ ...order, page, perPage: 10 }))
		} catch (err) {
			setError(err.message)
			setData(null)
		} finally {
			setIsLoading(false)
		}
	}, [order, page])

	useEffect(() => {
		load()
	}, [load])

	const handleOrderChange = (e) => {
		const { name, value } = e.target
		setOrder((prev) => ({ ...prev, [name]: value }))
		setPage(1)
	}

	const agents = data?.content || []

	return (
		<div>
			<PageHeader
				title="Pagadoria"
				subtitle="GET /backoffice/paying-agents/onboardings — onboardings de pagadoria, em andamento ou finalizados"
				actions={
					<>
						<Button variant="secondary" onClick={load}>
							<RefreshCw className="h-4 w-4" />
							Recarregar
						</Button>
						<Button onClick={() => setShowCreate(true)}>
							<Plus className="h-4 w-4" />
							Nova associacao
						</Button>
					</>
				}
			/>

			<Card className="mb-6">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					<Select
						id="orderColumn"
						name="orderColumn"
						label="Ordenar por"
						options={ORDER_COLUMNS}
						value={order.orderColumn}
						onChange={handleOrderChange}
					/>
					<Select
						id="orderDirection"
						name="orderDirection"
						label="Direcao"
						options={ORDER_DIRECTIONS}
						value={order.orderDirection}
						onChange={handleOrderChange}
					/>
				</div>
			</Card>

			<Alert onDismiss={() => setError('')}>{error}</Alert>
			<Alert type="success" onDismiss={() => setFeedback('')}>
				{feedback}
			</Alert>

			<div className="overflow-hidden rounded-lg bg-white shadow">
				{isLoading ? (
					<div className="flex h-64 items-center justify-center">
						<Loader size="lg" />
					</div>
				) : agents.length === 0 ? (
					<EmptyState message="Nenhum onboarding de pagadoria encontrado" />
				) : (
					<>
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Razao social
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											CNPJ
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Status onboarding
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Criado em
										</th>
										<th className="px-6 py-3" />
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 bg-white">
									{agents.map((agent) => (
										<tr key={agent.onboardingId} className="hover:bg-gray-50">
											<td className="px-6 py-4 text-sm text-gray-900">{agent.legalName || '—'}</td>
											<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
												{formatDocument(agent.document)}
											</td>
											<td className="px-6 py-4">
												<Badge tone={statusTone(agent.onboardingStatus)}>
													{agent.onboardingStatus}
												</Badge>
											</td>
											<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
												{formatDateTime(agent.createdAt)}
											</td>
											<td className="whitespace-nowrap px-6 py-4 text-right">
												<Button
													variant="secondary"
													onClick={() => navigate(`/pagadoria/${agent.onboardingId}`)}
												>
													Detalhes
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<Pagination
							currentPage={data?.currentPage || page}
							totalPages={data?.totalPages || 1}
							onPageChange={setPage}
						/>
					</>
				)}
			</div>

			{showCreate && (
				<CreateModal
					onClose={() => setShowCreate(false)}
					onCreated={(message) => {
						setShowCreate(false)
						setFeedback(message)
						load()
					}}
				/>
			)}
		</div>
	)
}

export default PayingAgentsList
